import './system-entities-index-list-item.scss';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  SxProps,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {Key, useMemo, useState} from 'react';
import moment from 'moment';
import {Pallete} from '../../theme/bluebird';
import {ComboActions} from '../combo-actions/combo-actions';
import {translations} from '../../i18n/translations';
import {FiEdit, FiTrash2} from 'react-icons/fi';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Theme} from '@mui/material/styles';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {DeleteUserModal} from '../delete-user-modal/delete-user-modal';
import {getLocalizedFieldValue} from '../../utils/functions/get-localized-field-value';
import {PaginatedTableColumn} from '../../interfaces/paginated-table-data';
import {UserType} from '../../backend/enums/user-type';
import {ColumnIdentifier} from '../../screens/private/office/professional-caregiver/professional-caregivers/_root/professional-caregivers.professional-caregiver.screen';
import {RelationshipInviteData} from '../../backend/interfaces/relationship-invite-data';
import {Invite} from '../../backend/interfaces/invite';

export const SystemEntitiesIndexListItem = ({
  data,
  columns,
  toLink,
  canWrite,
  canDelete,
  onDelete,
  onExportPdf,
}: {
  data: AffiliatedUser;
  columns: PaginatedTableColumn[];
  toLink?: string;
  canWrite?: boolean;
  canDelete?: boolean;
  onDelete?: (data: AffiliatedUser) => void;
  onExportPdf?: (data: AffiliatedUser) => void;
}) => {
  const history = useHistory();
  const {url} = useRouteMatch();

  // Translations
  const {t} = useTranslation();

  const caregiverRoles = useMemo(() => {
    return (
      data.user.type === UserType.Caregiver &&
      columns.find((col) => col.columnId === ColumnIdentifier.Roles)?.value
    );
  }, [columns, data.user.type]);
  const caregiverRolesText = caregiverRoles || '-';
  const caregiverEmailText = data.user.email || '-';

  // Dirty Hack
  const isPendingInvite =
    (data.user as unknown as Invite<RelationshipInviteData>).state === 'pending';

  function seniorAge(seniorInfo: AffiliatedUser): number {
    const today = moment();
    const birthDate = moment(seniorInfo.user.birthday);
    return today.diff(birthDate, 'years');
  }

  //Dialog
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);

  // Handle delete dialog opened state
  const handleDeleteDialogOpened = (opened: boolean) => {
    // Change dialog state
    setDeleteDialogOpened(opened);
  };

  const actions: Array<any> = [];
  if (!isPendingInvite && canWrite) {
    actions.push({
      label: t(translations.user_card.actions.edit),
      icon: {element: FiEdit},
      callback: () => history.push(`${url}/${data.user.id}`),
    });
  }
  if (canDelete) {
    actions.push({
      label: t(translations.user_card.actions.delete),
      icon: {element: FiTrash2},
      callback: () => handleDeleteDialogOpened(true),
    });
  }
  if (!isPendingInvite && onExportPdf) {
    actions.push({
      label: t(translations.export.senior_profile.save_pdf),
      icon: {element: SaveAltIcon},
      callback: () => onExportPdf(data),
    });
  }
  const hasActions = actions.length > 0;

  // Stylings
  const tableRowStyle: SxProps<Theme> = {
    marginLeft: '8px',
    '& .MuiTableCell-root': {
      paddingY: 0,
      height: '40px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      maxWidth: '300px',
    },
    '&:hover': {
      '& .MuiTableCell-root': {
        cursor: toLink ? 'pointer' : 'default',
        backgroundColor: Pallete.blue.hover,
        '&:first-child': {
          borderTopLeftRadius: '5px',
          borderBottomLeftRadius: '5px',
        },
        '&:last-child': {
          borderTopRightRadius: '5px',
          borderBottomRightRadius: '5px',
        },
      },
    },
  };

  const numberRowStyle: SxProps<Theme> = {
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: '18px',
    color: '#464646',
    textOverflow: 'ellipsis',
    textWrapMode: 'nowrap',
    overflow: 'hidden',
  };

  return (
    <>
      <DeleteUserModal
        deleteDialogOpened={deleteDialogOpened}
        setDeleteDialogOpened={setDeleteDialogOpened}
        handleDeleteDialogOpened={handleDeleteDialogOpened}
        data={data}
        onDelete={onDelete}
      />
      <TableRow sx={tableRowStyle}>
        <TableCell
          onClick={toLink ? () => history.push(toLink) : undefined}
          height="40px"
          width="59px"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Avatar
              alt={data.user.displayName || data.user.name}
              src={data.user.picture}
              sx={{
                border: `1.5px solid ${
                  data.entitySocialResponses.length > 0 &&
                  data.entitySocialResponses[0].foregroundColor
                }`,
              }}
            >
              {data.user.name.charAt(0)}
            </Avatar>
            <Typography
              sx={{
                marginLeft: '8px',
                fontSize: '18px',
                fontWeight: 600,
                lineHeight: '18px',
                color: '#464646',
              }}
            >
              {data.user.displayName || data.user.name}
            </Typography>
          </Box>
        </TableCell>
        <TableCell height="40px" width="125px">
          {data.user.type === UserType.Caregiver ? (
            <Typography
              title={caregiverRolesText !== '-' ? caregiverRolesText : undefined}
              sx={[numberRowStyle, {fontWeight: 500}]}
              align="center"
            >
              {caregiverRolesText}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                paddingX: 0,
              }}
            >
              {data.entitySocialResponses.length > 0 &&
                data.entitySocialResponses.map((socialResponse, i: Key | null | undefined) => (
                  <Chip
                    className="social-response-chip"
                    key={i}
                    label={getLocalizedFieldValue(socialResponse, 'abbreviation')}
                    color={'primary'}
                    sx={{
                      backgroundColor: socialResponse.backgroundColor,
                      color: socialResponse.foregroundColor,
                      fontSize: '12px',
                      fontWeight: 600,
                      lineHeight: '18px',
                      height: '20px',
                      '& .MuiChip-label': {
                        px: '8px',
                      },
                    }}
                  />
                ))}
            </Box>
          )}
        </TableCell>
        <TableCell height="40px" width="125px">
          {data.user.type === UserType.Caregiver ? (
            <Typography
              title={caregiverEmailText !== '-' ? caregiverEmailText : undefined}
              sx={[numberRowStyle, {fontWeight: 500}]}
              align="center"
            >
              {caregiverEmailText}
            </Typography>
          ) : (
            <Typography sx={numberRowStyle}>
              {data.user.birthday ? seniorAge(data) : '-'}
            </Typography>
          )}
        </TableCell>
        <TableCell height="40px" width="171px">
          {data.user.type === UserType.Caregiver ? (
            <Typography sx={numberRowStyle} style={{fontWeight: 500}}>
              {data.user.phoneNumber || '-'}
            </Typography>
          ) : (
            <Typography sx={numberRowStyle}>{data.affiliation.room || '-'}</Typography>
          )}
        </TableCell>
        <TableCell align="center" height="40px" width="125px">
          <Chip
            className="social-response-chip"
            label={
              data.user.isActive
                ? // If the user is active, show the active text
                  t(translations.seniors.root.state_value_true)
                : data.user.type === UserType.Caregiver && isPendingInvite
                  ? // If the user is a pending invite, show the pending invitation text
                    t(translations.cards.pendingInvitation)
                  : // Otherwise, show the inactive text
                    t(translations.seniors.root.state_value_false)
            }
            color={'primary'}
            sx={{
              backgroundColor: data.user.isActive ? '#CEE7CE' : Pallete.red.light,
              color: data.user.isActive ? 'rgba(23, 149, 119, 1)' : Pallete.red.base,
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '18px',
              height: '26px',
              '& .MuiChip-label': {
                px: '12px',
                py: '4px',
              },
            }}
          />
        </TableCell>
        {hasActions && (
          <TableCell align="right" height="40px" width="91px">
            <IconButton
              disableRipple
              aria-label="settings"
              sx={{
                '& .MuiSvgIcon-root': {
                  fontSize: '32px',
                },
              }}
            >
              <ComboActions
                className="actions"
                wrapperSX={{
                  color: Pallete.gray.light,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
                triggerSX={{height: '32px', width: '32px'}}
                actions={actions}
              />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
    </>
  );
};
